/**
 * German typographic double quotes — the single source of truth for both the
 * rich text editor and plain `<input>` fields.
 *
 * The pair is `„…“`: U+201E (DOUBLE LOW-9) opens, U+201C (LEFT DOUBLE) closes.
 * Note that the closer is the *left* double quotation mark; U+201D (RIGHT
 * DOUBLE) is the English closer and is treated as input to be repaired, never
 * as output.
 *
 * This module is imported by Node-side packages (cron, api), so it must stay
 * free of DOM APIs. The HTML-aware paste repair lives in the richtext-editor
 * module for that reason.
 */
/** U+201E — opens a German quotation. */
export const GERMAN_OPENER = '\u201E';
/** U+201C — closes a German quotation. */
export const GERMAN_CLOSER = '\u201C';
const STRAIGHT_QUOTE = '"';
/** Double-quote variants mapped to straight `"` before alternation is applied. */
const QUOTE_NORMALIZE_PATTERN = /[\u201C\u201D\u201E\u201F\u00AB\u00BB\u2039\u203A\u275D\u275E\uFF02]/g;
/** The same set plus straight `"` — a cheap "is there anything to do?" test. */
const ANY_QUOTE_PATTERN = /["\u201C\u201D\u201E\u201F\u00AB\u00BB\u2039\u203A\u275D\u275E\uFF02]/;
function transformChar(char, isOpen) {
    if (char === GERMAN_OPENER) {
        return { char, isOpen: true };
    }
    // U+201D is the English closer: accepted as input, always emitted as U+201C.
    if (char === GERMAN_CLOSER || char === '\u201D') {
        return { char: GERMAN_CLOSER, isOpen: false };
    }
    if (char === STRAIGHT_QUOTE) {
        return isOpen ? { char: GERMAN_CLOSER, isOpen: false } : { char: GERMAN_OPENER, isOpen: true };
    }
    return { char, isOpen };
}
/**
 * True when `value` holds any character this module would rewrite. Used to skip
 * work on the overwhelming majority of keystrokes, which contain no quote at all.
 */
export function containsTransformableQuote(value) {
    return ANY_QUOTE_PATTERN.test(value);
}
/**
 * Returns whether the next straight `"` in `value` should become an opener (`true`)
 * or a closer (`false`), optionally continuing from a prior open/closed state.
 */
export function getGermanQuoteOpenState(value, initialOpen = false) {
    let isOpen = initialOpen;
    for (const char of value) {
        ({ isOpen } = transformChar(char, isOpen));
    }
    return isOpen;
}
/**
 * Like {@link getGermanQuoteOpenState} but only considers the current line — text
 * after the last newline. Orphan open quotes on a previous line do not carry over.
 */
export function getGermanQuoteOpenStateOnCurrentLine(value) {
    const currentLine = value.split('\n').pop() ?? '';
    return getGermanQuoteOpenState(currentLine);
}
/**
 * Like {@link transformToGermanQuotes} but accepts an initial open/closed state and
 * returns the final state — for block-level processing across ProseMirror text nodes.
 * Does not normalize variants; call {@link normalizeQuotesForTransform} first if needed.
 *
 * `resetOnNewline` mirrors ProseMirror textblock boundaries and is what the paste path
 * wants. A plain `<textarea>` is one field, not a stack of blocks, so the input path
 * passes `false` — otherwise a quotation spanning a soft line break has its closer
 * re-opened.
 */
export function transformToGermanQuotesWithState(value, initialOpen = false, resetOnNewline = true) {
    let result = '';
    let isOpen = initialOpen;
    for (const char of value) {
        if (char === '\n' && resetOnNewline) {
            result += char;
            isOpen = false;
            continue;
        }
        const next = transformChar(char, isOpen);
        result += next.char;
        isOpen = next.isOpen;
    }
    return { text: result, isOpen };
}
/**
 * Maps known double-quote variants to straight `"` so a subsequent
 * {@link transformToGermanQuotes} pass can repair malformed quotes.
 */
export function normalizeQuotesForTransform(value) {
    return value.replace(QUOTE_NORMALIZE_PATTERN, STRAIGHT_QUOTE);
}
/**
 * True when `value` contains only balanced canonical `„…“` pairs (or no quotes).
 * Used to leave already-correct Word/HTML paste alone.
 */
export function hasBalancedGermanQuotesOnly(value) {
    if (value.includes(STRAIGHT_QUOTE))
        return false;
    if (/[\u201D\u201F\u00AB\u00BB\u2039\u203A\u275D\u275E\uFF02]/.test(value))
        return false;
    let isOpen = false;
    for (const char of value) {
        if (char === GERMAN_OPENER) {
            if (isOpen)
                return false;
            isOpen = true;
        }
        else if (char === GERMAN_CLOSER) {
            if (!isOpen)
                return false;
            isOpen = false;
        }
    }
    return !isOpen;
}
/**
 * Normalizes every double-quote variant in `value` to the German pair `„…“`.
 *
 * For form fields: alternation runs continuously over the whole value, so a quotation
 * that spans a soft line break in a textarea keeps its closer. Use
 * {@link repairGermanQuotesForPaste} for block-structured content instead.
 *
 * Example: `"foo" »bar« “baz”` becomes `„foo“ „bar“ „baz“`.
 */
export function transformToGermanQuotes(value) {
    // Already canonical: nothing to do, and skipping keeps every keystroke a no-op.
    if (hasBalancedGermanQuotesOnly(value))
        return value;
    return transformToGermanQuotesWithState(normalizeQuotesForTransform(value), false, false).text;
}
/**
 * Normalizes every double-quote variant to `„…“`, restarting alternation at each line
 * break — the right behaviour for pasted, block-structured content.
 *
 * Already-canonical text is returned untouched, matching
 * {@link repairGermanQuotesForPasteHtml}'s per-node behaviour. Without this, a correct
 * quotation spanning a line break would be flattened and its closer re-opened — so text
 * copied out of a form field, which preserves such a pair, would be corrupted on the way
 * back into the editor.
 */
export function repairGermanQuotesForPaste(value) {
    if (hasBalancedGermanQuotesOnly(value))
        return value;
    return transformToGermanQuotesWithState(normalizeQuotesForTransform(value), false, true).text;
}
//# sourceMappingURL=germanQuotes.js.map